/**
 * Converts the nested BidPreviewSchema shape (sections → categories → line_items)
 * returned by the LLM's streaming snapshots into the flat ApiBid shape
 * (line_groups[] + line_items[]) that AtomicBidCreator / useLocalBidState expects.
 *
 * This mirrors the backend's `convert_bid_preview_to_flat_preview` logic.
 */

let groupIdCounter = 10_000_001;
let itemIdCounter = 20_000_001;

export function convertNestedPreviewToFlatBid(preview, readOnly = false) {
    if (!preview) return null;

    // If already in flat format (has line_groups), pass through
    if (preview.line_groups) return preview;

    // Must have nested sections
    if (!preview.sections && !preview.line_items_grouped) return null;

    const sections = preview.sections || preview.line_items_grouped || [];
    const groups = [];
    const items = [];

    // Reset counters each call to keep IDs deterministic within a single preview
    groupIdCounter = 10_000_001;
    itemIdCounter = 20_000_001;

    for (const section of sections) {
        const secId = groupIdCounter++;
        groups.push({
            id: secId,
            name: section.name || "Untitled Section",
            custom_id: section.custom_id || "",
            parent_id: null,
            sort_order: section.sort_order || 0,
            rate_type: section.rate_type || "Days",
            is_internal: false,
            markup: 0,
            markup_amount: 0,
            budget_buffer: 0,
            budget_buffer_amount: 0,
            client_budget: 0,
            client_quoted: 0,
        });

        const categories = section.categories || section.children || [];
        for (const category of categories) {
            const catId = groupIdCounter++;
            groups.push({
                id: catId,
                name: category.name || "Untitled Category",
                custom_id: category.custom_id || "",
                parent_id: secId,
                sort_order: category.sort_order || 0,
                rate_type: category.rate_type || "Days",
                is_internal: false,
                markup: 0,
                markup_amount: 0,
                budget_buffer: 0,
                budget_buffer_amount: 0,
                client_budget: 0,
                client_quoted: 0,
            });

            const lineItems = category.line_items || [];
            for (const item of lineItems) {
                const itemId = itemIdCounter++;
                items.push({
                    id: itemId,
                    line_group_id: catId,
                    name: item.name || "Untitled Item",
                    custom_id: item.custom_id || "",
                    days: Number(item.days) || 1,
                    quantity: Number(item.quantity) || 0,
                    vendor_rate: Number(item.vendor_rate) || 0,
                    rate_type: item.rate_type || "Days",
                    vendor_currency: item.vendor_currency || "USD",
                    is_internal: item.is_internal || false,
                    markup: Number(item.markup) || 0,
                    markup_amount: 0,
                    adjustment: 0,
                    ot_1_5: 0,
                    ot_2_0: 0,
                    exchange_rate_buffer: 0,
                    budget_buffer: 0,
                    budget_buffer_amount: 0,
                    sort_order: item.sort_order || 0,
                    internal_notes: "",
                    external_notes: "",
                    service_item_id: null,
                    client_budget: 0,
                    client_budget_buffered: 0,
                    client_quoted: 0,
                });
            }
        }
    }

    return {
        id: 0,
        name: preview.name || "Generated Bid",
        status: readOnly ? "ReadOnly" : "Drafts",
        bid_type: "Main",
        line_groups: groups,
        line_items: items,
        addons: [],
        visible_columns: [],
        sticky_columns: [],
        project_id: preview.project_id || null,
        assumptions: preview.assumptions || null,
        inclusions: preview.inclusions || null,
        exclusions: preview.exclusions || null,
    };
}
